import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import WorkGallery from '../components/WorkGallery';

const Contact = () => {
  const workImages = [
    '/siteimages/image_0001.jpg',
    '/siteimages/image_0012.jpg',
    '/siteimages/image_0017.jpeg',
    '/siteimages/image_0020.jpg',
    '/siteimages/image_0023.jpg',
    '/siteimages/image_0025.jpg',
  ];

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Form submission logic would go here
    alert('Thank you for your message! We will get back to you soon.');
    setFormData({ name: '', email: '', phone: '', service: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-dark-navy to-dark-teal text-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-6xl font-heading font-bold mb-6">
            Get in <span className="text-bmw-blue">Touch</span>
          </h1>
          <p className="text-lg md:text-xl text-soft-grey max-w-3xl">
            Contact us today for a free quote or to discuss your vehicle repair requirements.
          </p>
        </div>
      </section>

      {/* Contact Form and Info */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-heading font-bold text-dark-navy mb-6">
                Send Us a <span className="text-bmw-blue">Message</span>
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-dark-navy mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-soft-grey rounded-lg focus:border-bmw-blue focus:outline-none transition"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-dark-navy mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-soft-grey rounded-lg focus:border-bmw-blue focus:outline-none transition"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-dark-navy mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-soft-grey rounded-lg focus:border-bmw-blue focus:outline-none transition"
                    placeholder="07XXX XXXXXX"
                  />
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-semibold text-dark-navy mb-2">
                    Service Required
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-soft-grey rounded-lg focus:border-bmw-blue focus:outline-none transition"
                  >
                    <option value="">Select a service...</option>
                    <option value="scratch-paint">Scratch & Paint Repair</option>
                    <option value="dent-repair">Dent Repair & Removal</option>
                    <option value="alloy-wheel">Alloy Wheel Repair</option>
                    <option value="bumper-repair">Bumper Repair</option>
                    <option value="ceramic-coating">Ceramic Coating</option>
                    <option value="stone-chip">Stone Chip & Touch Up</option>
                    <option value="general-inquiry">General Inquiry</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-dark-navy mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    rows="6"
                    className="w-full px-4 py-3 border-2 border-soft-grey rounded-lg focus:border-bmw-blue focus:outline-none transition resize-none"
                    placeholder="Please describe the work you need done..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full bg-racing-red text-white px-8 py-4 rounded-lg font-semibold hover:bg-red-700 transition shadow-lg inline-flex items-center justify-center gap-2"
                >
                  Send Message
                  <Send size={20} />
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-heading font-bold text-dark-navy mb-6">
                Contact <span className="text-bmw-blue">Information</span>
              </h2>

              <div className="space-y-6">
                {/* Phone */}
                <div className="bg-off-white p-6 rounded-lg">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-bmw-blue text-white rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone size={24} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-dark-navy mb-2">Phone</h3>
                      <p className="text-gray-600 mb-2">Call us during business hours for immediate assistance.</p>
                      <a href="tel:08000452446" className="text-bmw-blue font-semibold hover:underline block">
                        0800 045 2446
                      </a>
                      <a href="tel:08000453447" className="text-bmw-blue font-semibold hover:underline block">
                        0800 045 3447
                      </a>
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div className="bg-off-white p-6 rounded-lg">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-bmw-blue text-white rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail size={24} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-dark-navy mb-2">Email</h3>
                      <p className="text-gray-600 mb-2">Send us an email and we'll respond within 24 hours.</p>
                      <a href="mailto:support@GBWBodyworksLimited.co.uk" className="text-bmw-blue font-semibold hover:underline block break-all">
                        support@GBWBodyworksLimited.co.uk
                      </a>
                      <a href="mailto:help@GBWBodyworksLimited.co.uk" className="text-bmw-blue font-semibold hover:underline block break-all">
                        help@GBWBodyworksLimited.co.uk
                      </a>
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div className="bg-off-white p-6 rounded-lg">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-bmw-blue text-white rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin size={24} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-dark-navy mb-2">Location</h3>
                      <p className="text-gray-600 mb-2">Visit us at our workshop in Leicestershire.</p>
                      <address className="not-italic text-dark-navy font-semibold">
                        85-87 Station Road<br />
                        Countesthorpe<br />
                        Leicestershire LE8 5TD
                      </address>
                    </div>
                  </div>
                </div>

                {/* Hours */}
                <div className="bg-off-white p-6 rounded-lg">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-bmw-blue text-white rounded-full flex items-center justify-center flex-shrink-0">
                      <Clock size={24} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-dark-navy mb-2">Opening Hours</h3>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Monday - Friday:</span>
                          <span className="font-semibold text-dark-navy">09:00 – 17:00</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Saturday:</span>
                          <span className="font-semibold text-dark-navy">09:00 – 17:00</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Sunday:</span>
                          <span className="font-semibold text-dark-navy">Closed</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-off-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-heading font-bold text-dark-navy mb-8 text-center">
            Find <span className="text-bmw-blue">Us</span>
          </h2>
          <div className="max-w-6xl mx-auto">
            <div className="bg-soft-grey rounded-lg overflow-hidden shadow-lg" style={{ height: '450px' }}>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2422.8!2d-1.1383!3d52.5661!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNTLCsDMzJzU4LjAiTiAxwrAwOCcxNy45Ilc!5e0!3m2!1sen!2suk!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="GBW Bodyworks Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Work Gallery */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-dark-navy mb-4">
              Recent <span className="text-bmw-blue">Projects</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              See examples of our professional bodywork repairs and restorations.
            </p>
          </div>
          <WorkGallery images={workImages} title="" />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-bmw-blue text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
            Prefer to Speak Directly?
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">
            Give us a call and speak with one of our friendly team members who will be happy to answer your questions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:08000452446" 
              className="bg-racing-red text-white px-8 py-4 rounded-lg font-semibold hover:bg-red-700 transition shadow-lg inline-flex items-center justify-center gap-2"
            >
              <Phone />
              0800 045 2446
            </a>
            <a 
              href="tel:08000453447" 
              className="bg-white text-bmw-blue px-8 py-4 rounded-lg font-semibold hover:bg-off-white transition shadow-lg inline-flex items-center justify-center gap-2"
            >
              <Phone />
              0800 045 3447
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
